import { SensorsBillingStatsColumn } from 'src/api'
import { SearchParamsKeys } from 'src/enums'

export type BillingStatRowFilters = Partial<
  Record<SearchParamsKeys.ActivationStatus | SearchParamsKeys.ActivatedMoreThanMonths | SearchParamsKeys.NoPingMoreThanMonths, string>
>

// Params cleared before applying new billing filters — shared by the filter selects and the clickable stats rows
export const billingStatsFilterResetKeys = [
  SearchParamsKeys.ActivatedMoreThanMonths,
  SearchParamsKeys.NoPingMoreThanMonths,
  SearchParamsKeys.ActivationStatus,
  SearchParamsKeys.Page
]

export interface BillingStatRow {
  key: keyof SensorsBillingStatsColumn
  title: string
  thingspaceHint: string
  climitHint: string
  // Params applied to the "Sensors" tables below when the row title is clicked. An empty object clears all three filters.
  filters: BillingStatRowFilters
  // Optional staging counterpart of `key`. When set, the row shows the staging subset next to the value ("456 (5 staging)").
  stagingKey?: keyof SensorsBillingStatsColumn
}

export const billingStatsRows: BillingStatRow[] = [
  {
    key: 'total',
    title: 'Total',
    thingspaceHint: 'Total amount of sensors added to the CLIMIT ThingSpace account',
    climitHint: "Total amount of sensors added to the CLIMIT platform (sensors which are purchased but not imported won't be displayed here)",
    filters: {}
  },
  {
    key: 'active',
    title: 'Active / Live',
    thingspaceHint: 'Amount of active sensors',
    climitHint: 'Amount of sensors which sent us a ping within a day (live)',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active' }
  },
  {
    key: 'deactivated',
    title: 'Deactivated',
    thingspaceHint: 'Amount of deactivated sensors',
    climitHint: 'Amount of deactivated sensors present in the CLIMIT DB',
    filters: { [SearchParamsKeys.ActivationStatus]: 'deactive' }
  },
  {
    // The sensors tables have no "activated less than N months" filter, so clicking this row shows all active sensors
    key: 'activatedUnder24Months',
    title: 'Activated < 24 months ago',
    thingspaceHint: 'Amount of active sensors which were activated less than 24 months ago',
    climitHint: 'Amount of active sensors which were activated less than 24 months ago',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active' }
  },
  {
    key: 'activatedOver24Months',
    title: 'Activated > 24 months ago',
    thingspaceHint: 'Amount of active sensors which were activated more than 24 months ago',
    climitHint: 'Amount of active sensors which were activated more than 24 months ago',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.ActivatedMoreThanMonths]: '24' }
  },
  {
    key: 'activatedOver24MonthsNoPingOver3Months',
    title: 'Activated > 24 months ago & no ping for more than 3 months',
    thingspaceHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 3 months',
    climitHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 3 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.ActivatedMoreThanMonths]: '24', [SearchParamsKeys.NoPingMoreThanMonths]: '3' },
    stagingKey: 'activatedOver24MonthsNoPingOver3MonthsStaging'
  },
  {
    key: 'activatedOver24MonthsNoPingOver6Months',
    title: 'Activated > 24 months ago & no ping for more than 6 months',
    thingspaceHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 6 months',
    climitHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 6 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.ActivatedMoreThanMonths]: '24', [SearchParamsKeys.NoPingMoreThanMonths]: '6' },
    stagingKey: 'activatedOver24MonthsNoPingOver6MonthsStaging'
  },
  {
    key: 'activatedOver24MonthsNoPingOver9Months',
    title: 'Activated > 24 months ago & no ping for more than 9 months',
    thingspaceHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 9 months',
    climitHint: 'Amount of active sensors which were activated more than 24 months ago and have not pinged for more than 9 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.ActivatedMoreThanMonths]: '24', [SearchParamsKeys.NoPingMoreThanMonths]: '9' },
    stagingKey: 'activatedOver24MonthsNoPingOver9MonthsStaging'
  },
  {
    key: 'noPingOver3Months',
    title: 'No ping for more than 3 months',
    thingspaceHint: 'Amount of active sensors which have not pinged for more than 3 months',
    climitHint: 'Amount of active sensors which have not pinged for more than 3 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.NoPingMoreThanMonths]: '3' },
    stagingKey: 'noPingOver3MonthsStaging'
  },
  {
    key: 'noPingOver6Months',
    title: 'No ping for more than 6 months',
    thingspaceHint: 'Amount of active sensors which have not pinged for more than 6 months',
    climitHint: 'Amount of active sensors which have not pinged for more than 6 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.NoPingMoreThanMonths]: '6' },
    stagingKey: 'noPingOver6MonthsStaging'
  },
  {
    key: 'noPingOver9Months',
    title: 'No ping for more than 9 months',
    thingspaceHint: 'Amount of active sensors which have not pinged for more than 9 months',
    climitHint: 'Amount of active sensors which have not pinged for more than 9 months',
    filters: { [SearchParamsKeys.ActivationStatus]: 'active', [SearchParamsKeys.NoPingMoreThanMonths]: '9' },
    stagingKey: 'noPingOver9MonthsStaging'
  }
]

// Renders a stat value with its staging subset, e.g. "456 (5 staging)". Rows without a staging
// counterpart (staging undefined) show the bare value; missing values render as a dash.
export const formatStatWithStaging = (value?: number | null, staging?: number | null): string => {
  if (value === null || value === undefined) return '-'
  if (staging === null || staging === undefined) return `${value}`
  return `${value} (${staging} staging)`
}

export interface BillingStatsGridRow extends BillingStatRow {
  thingspace?: number
  climit?: number
  thingspaceStaging?: number
  climitStaging?: number
}

export const buildBillingStatsGridRows = (thingspace?: SensorsBillingStatsColumn, climit?: SensorsBillingStatsColumn): BillingStatsGridRow[] =>
  billingStatsRows.map((row) => ({
    ...row,
    thingspace: thingspace?.[row.key],
    climit: climit?.[row.key],
    thingspaceStaging: row.stagingKey ? thingspace?.[row.stagingKey] : undefined,
    climitStaging: row.stagingKey ? climit?.[row.stagingKey] : undefined
  }))
